(() => {
  'use strict';

  const SECTION_CONFIG = [
    { key: 'hero', name: 'hero', state: 'wave', triggerRatio: 0.3 },
    { key: 'visa', name: 'visa', state: 'passport', triggerRatio: 0.2 },
    { key: 'medical', name: 'medical', state: 'medical', triggerRatio: 0.35 },
    { key: 'travel', name: 'travel-planner', state: 'guide', triggerRatio: 0.35 },
    { key: 'photo_wall', name: 'photo-wall', state: 'camera', triggerRatio: 0.35 },
    { key: 'contact', name: 'contact', state: 'phone', triggerRatio: 0.35 }
  ];
  const SCROLL_SETTLE_DELAY = 80;
  const RESIZE_DELAY = 150;
  const INTRO_STORAGE_KEY = 'pandaGuideQiBaoIntroComplete';
  const instances = new WeakMap();

  class PandaGuide {
    #root;
    #sectionId;
    #images = new Map();
    #sections = [];
    #menu = null;
    #menuItems = new Map();
    #menuOpen = false;
    #menuIdleTimer = 0;
    #programmaticScrollUntil = 0;
    #indicator = null;
    #platform = null;
    #bodyGlow = null;
    #indicatorTimer = 0;
    #indicatorDelayTimer = 0;
    #secondReminderTimer = 0;
    #firstIndicatorShown = false;
    #secondReminderResolved = false;
    #reminderSections = new Set();
    #pulseTimer = 0;
    #pulseEndTimer = 0;
    #visibleBounds = new Map();
    #bubble = null;
    #bubbleSeen = new Set();
    #bubbleTimer = 0;
    #currentBubbleSection = null;
    #introActive = false;
    #idleReminderTimer = 0;
    #idleReminderShown = false;
    #currentState = 'idle';
    #currentSectionState = 'idle';
    #lastScrollY = window.scrollY;
    #scrollFrame = 0;
    #scrollTimer = 0;
    #resizeTimer = 0;
    #editorTimer = 0;
    #controller = new AbortController();
    #destroyed = false;
    #initialized = false;

    constructor(root) {
      this.#root = root;
      this.#sectionId = String(root.dataset.sectionId || '');
      this.#currentState = root.dataset.current || 'idle';
    }

    init() {
      if (this.#initialized || this.#destroyed) return;

      this.#initialized = true;
      this.#cacheImages();
      this.#cacheMenu();
      this.#cacheBubble();
      this.#cacheIndicator();
      this.#cachePlatform();
      this.#cacheBodyGlow();
      this.#introActive = this.#shouldShowFirstVisitGuide();
      this.#configureAccessibility();
      this.#bindEvents();
      this.refresh();
      if (this.#introActive) this.#startFirstVisitGuide();
      else this.#registerInteraction();
      this.#startClickIndicator();
      this.#schedulePulse();
    }

    refresh() {
      if (this.#destroyed) return;

      const scrollY = window.scrollY;
      const sections = [];

      SECTION_CONFIG.forEach((config) => {
        const element = this.#findShopifySection(config.key);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const top = rect.top + scrollY;

        sections.push({
          key: config.key,
          name: config.name,
          state: config.state,
          element,
          top,
          bottom: top + rect.height,
          height: rect.height,
          triggerTop: top - window.innerHeight * (1 - config.triggerRatio)
        });
      });

      this.#sections = sections;
      this.#lastScrollY = scrollY;
      this.#currentSectionState = this.#getSectionState(scrollY);
      if (!this.#introActive) this.setState(this.#currentSectionState);
      this.#updateActiveMenuItem();
      this.#positionMenu();
      this.#updateSectionBubble();
      this.#positionBubble();
      this.#positionIndicator();
      this.#positionPlatform();
      this.#positionBodyGlow();
      this.#trackReminderSection();
    }

    setState(targetState) {
      if (this.#destroyed || !targetState || targetState === this.#currentState) return;

      const targetImage = this.#images.get(targetState);
      if (!targetImage || targetImage.dataset.fallback === 'true') return;

      const currentImage = this.#images.get(this.#currentState);
      if (currentImage !== targetImage) currentImage?.classList.remove('is-active');
      targetImage.classList.add('is-active');

      const previousState = this.#currentState;
      this.#currentState = targetState;
      this.#root.dataset.current = targetState;
      this.#setAnimation(targetState, previousState);
      if (this.#menuOpen) this.#positionMenu();
      if (this.#bubble?.classList.contains('is-visible')) this.#positionBubble();
      if (this.#indicator?.classList.contains('is-visible')) this.#positionIndicator();
      this.#positionPlatform();
      this.#positionBodyGlow();
    }

    destroy() {
      if (this.#destroyed) return;

      this.#destroyed = true;
      this.#controller.abort();
      window.clearTimeout(this.#scrollTimer);
      window.clearTimeout(this.#resizeTimer);
      window.clearTimeout(this.#editorTimer);
      window.clearTimeout(this.#bubbleTimer);
      window.clearTimeout(this.#menuIdleTimer);
      window.clearTimeout(this.#idleReminderTimer);
      window.clearTimeout(this.#indicatorTimer);
      window.clearTimeout(this.#indicatorDelayTimer);
      window.clearTimeout(this.#secondReminderTimer);
      window.clearTimeout(this.#pulseTimer);
      window.clearTimeout(this.#pulseEndTimer);
      if (this.#scrollFrame) window.cancelAnimationFrame(this.#scrollFrame);

      this.#scrollFrame = 0;
      this.#sections = [];
      this.#images.clear();
      this.#menuItems.clear();
      this.#bubbleSeen.clear();
      this.#visibleBounds.clear();
      this.#reminderSections.clear();
      instances.delete(this.#root);
      this.#root.removeAttribute('data-panda-initialized');
    }

    #findShopifySection(key) {
      const staticId = `shopify-section-${key}`;
      const staticSection = document.getElementById(staticId);
      if (staticSection) return staticSection;

      return document.querySelector(
        `[id^="shopify-section-"][id$="__${key}"]`
      );
    }

    #cacheImages() {
      this.#root.querySelectorAll('[data-panda-state]').forEach((image) => {
        this.#images.set(image.dataset.pandaState, image);
      });
    }

    #cacheMenu() {
      this.#menu = document.getElementById(`PandaGuideMenu-${this.#sectionId}`);
      this.#menu?.querySelectorAll('[data-panda-menu-item]').forEach((item) => {
        this.#menuItems.set(item.dataset.pandaMenuItem, item);
      });
    }

    #cacheBubble() {
      this.#bubble = document.getElementById(`PandaGuideBubble-${this.#sectionId}`);
    }

    #cacheIndicator() {
      this.#indicator = document.getElementById(`QiClickIndicator-${this.#sectionId}`);
    }

    #cachePlatform() {
      this.#platform = document.getElementById(`QiPandaPlatform-${this.#sectionId}`);
    }

    #cacheBodyGlow() {
      this.#bodyGlow = document.getElementById(`QiPandaBodyGlow-${this.#sectionId}`);
    }

    #configureAccessibility() {
      if (this.#root.dataset.click === 'true') {
        this.#root.setAttribute('role', 'button');
        this.#root.setAttribute('tabindex', '0');
        this.#root.setAttribute('aria-expanded', 'false');
        return;
      }

      this.#root.removeAttribute('role');
      this.#root.removeAttribute('tabindex');
    }

    #bindEvents() {
      const signal = this.#controller.signal;

      window.addEventListener('scroll', this.#onScroll, {
        passive: true,
        signal
      });
      window.addEventListener('resize', this.#onResize, {
        passive: true,
        signal
      });
      window.visualViewport?.addEventListener('resize', this.#onResize, {
        passive: true,
        signal
      });
      window.visualViewport?.addEventListener('scroll', this.#onResize, {
        passive: true,
        signal
      });
      window.addEventListener('load', this.#onPageLoad, {
        once: true,
        signal
      });

      document.addEventListener('shopify:section:load', this.#onEditorChange, { signal });
      document.addEventListener('shopify:section:unload', this.#onSectionUnload, { signal });
      document.addEventListener('shopify:section:select', this.#onEditorChange, { signal });
      document.addEventListener('shopify:section:deselect', this.#onEditorChange, { signal });
      document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
      document.addEventListener('keydown', this.#onDocumentKeydown, { signal });

      if (this.#root.dataset.click === 'true') {
        this.#root.addEventListener('click', this.#onActivate, { signal });
        this.#root.addEventListener('keydown', this.#onKeydown, { signal });
        this.#menu?.addEventListener('click', this.#onMenuClick, { signal });
        this.#menu?.addEventListener('keydown', this.#onMenuKeydown, { signal });
        this.#menu?.addEventListener('pointermove', this.#onMenuInteraction, {
          passive: true,
          signal
        });
      }
    }

    #onScroll = () => {
      if (this.#scrollFrame || this.#destroyed) return;

      this.#scrollFrame = window.requestAnimationFrame(() => {
        this.#scrollFrame = 0;

        const scrollY = window.scrollY;
        const delta = Math.abs(scrollY - this.#lastScrollY);
        if (
          delta > 0 &&
          this.#menuOpen &&
          this.#root.dataset.closeMenuOnScroll === 'true' &&
          Date.now() > this.#programmaticScrollUntil
        ) {
          this.#setMenuOpen(false);
        }
        if (delta > 0) this.#registerInteraction();
        this.#lastScrollY = scrollY;
        this.#currentSectionState = this.#getSectionState(scrollY);
        this.#updateActiveMenuItem();
        this.#updateSectionBubble();
        this.#trackReminderSection();

        if (delta > 0) {
          this.setState('walk');
          window.clearTimeout(this.#scrollTimer);
          this.#scrollTimer = window.setTimeout(() => {
            this.#scrollTimer = 0;
            this.setState(this.#currentSectionState);
          }, SCROLL_SETTLE_DELAY);
          return;
        }

        if (!this.#scrollTimer) this.setState(this.#currentSectionState);
      });
    };

    #getSectionState(scrollPosition) {
      if (!this.#sections.length) return 'idle';
      if (scrollPosition < this.#sections[0].triggerTop) return this.#sections[0].state;

      for (let index = 0; index < this.#sections.length; index += 1) {
        const section = this.#sections[index];
        const nextTrigger = this.#sections[index + 1]?.triggerTop ?? Number.POSITIVE_INFINITY;

        if (scrollPosition >= section.triggerTop && scrollPosition < nextTrigger) {
          return section.state;
        }
      }

      return this.#sections[this.#sections.length - 1].state;
    }

    #onResize = () => {
      window.clearTimeout(this.#resizeTimer);
      this.#resizeTimer = window.setTimeout(() => {
        this.#resizeTimer = 0;
        this.refresh();
      }, RESIZE_DELAY);
    };

    #onPageLoad = () => {
      this.refresh();
    };

    #onEditorChange = () => {
      window.clearTimeout(this.#editorTimer);
      this.#editorTimer = window.setTimeout(() => {
        this.#editorTimer = 0;
        this.refresh();
      }, 0);
    };

    #onSectionUnload = (event) => {
      if (String(event.detail?.sectionId || '') === this.#sectionId) {
        this.destroy();
        return;
      }

      this.#onEditorChange();
    };

    #onActivate = () => {
      this.#completeFirstVisitGuide();
      this.#registerInteraction();
      this.#setMenuOpen(!this.#menuOpen, { focusMenu: true });
    };

    #onKeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.#onActivate();
    };

    #onMenuClick = (event) => {
      const item = event.target.closest('[data-panda-menu-item]');
      if (!item || !this.#menu?.contains(item)) return;

      const section = this.#sections.find((entry) => entry.key === item.dataset.pandaMenuItem);
      if (!section) return;

      this.#programmaticScrollUntil = Date.now() + 1500;
      if (this.#root.dataset.closeMenuAfterSelection === 'true') {
        this.#setMenuOpen(false);
      } else {
        this.#resetMenuIdleTimer();
      }
      section.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    #onMenuKeydown = (event) => {
      const items = Array.from(this.#menuItems.values()).filter((item) => !item.disabled);
      const currentIndex = items.indexOf(document.activeElement);
      if (currentIndex < 0 || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      let nextIndex = currentIndex;
      if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % items.length;
      if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + items.length) % items.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = items.length - 1;
      items[nextIndex]?.focus();
    };

    #onDocumentPointerDown = (event) => {
      this.#registerInteraction();
      if (this.#menu?.contains(event.target)) this.#resetMenuIdleTimer();
      if (!this.#menuOpen || this.#root.contains(event.target) || this.#menu?.contains(event.target)) return;
      this.#setMenuOpen(false);
    };

    #onDocumentKeydown = (event) => {
      this.#registerInteraction();
      if (this.#menuOpen) this.#resetMenuIdleTimer();
      if (event.key !== 'Escape' || !this.#menuOpen) return;
      event.preventDefault();
      this.#setMenuOpen(false, { returnFocus: true });
    };

    #onMenuInteraction = () => {
      this.#resetMenuIdleTimer();
    };

    #setMenuOpen(open, options = {}) {
      if (!this.#menu || this.#menuOpen === open) return;

      this.#menuOpen = open;
      this.#menu.classList.toggle('is-open', open);
      this.#menu.setAttribute('aria-hidden', String(!open));
      this.#root.setAttribute('aria-expanded', String(open));
      this.#root.setAttribute('aria-label', open ? 'Close Qi Bao guide menu' : 'Open Qi Bao guide menu');
      this.#menuItems.forEach((item) => {
        item.tabIndex = open ? 0 : -1;
      });
      if (open) this.#resetMenuIdleTimer();
      else {
        window.clearTimeout(this.#menuIdleTimer);
        this.#menuIdleTimer = 0;
      }

      if (open && options.focusMenu) {
        window.requestAnimationFrame(() => {
          this.#positionMenu();
          (this.#menu.querySelector('.is-active') || this.#menuItems.values().next().value)?.focus();
        });
      } else if (!open && options.returnFocus) {
        this.#root.focus();
      }
    }

    #resetMenuIdleTimer() {
      window.clearTimeout(this.#menuIdleTimer);
      this.#menuIdleTimer = 0;
      if (!this.#menuOpen) return;

      const delay = Number.parseInt(this.#root.dataset.menuAutoCloseDelay, 10) || 8000;
      this.#menuIdleTimer = window.setTimeout(() => {
        this.#menuIdleTimer = 0;
        this.#setMenuOpen(false);
      }, delay);
    }

    #positionMenu() {
      if (!this.#menu) return;

      const viewportMargin = 24;
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportWidth = viewport?.width || window.innerWidth;

      this.#menu.style.removeProperty('top');
      this.#menu.style.removeProperty('bottom');
      this.#menu.style.removeProperty('left');
      this.#menu.style.removeProperty('right');

      const menuRect = this.#menu.getBoundingClientRect();
      const activeAsset = this.#root.querySelector('.panda-guide__image.is-active .panda-guide__asset');
      const pandaRect = this.#root.getBoundingClientRect();
      const naturalWidth = activeAsset?.naturalWidth || pandaRect.width;
      const naturalHeight = activeAsset?.naturalHeight || pandaRect.height;
      const imageScale = Math.min(
        pandaRect.width / naturalWidth,
        pandaRect.height / naturalHeight
      );
      const renderedImageWidth = naturalWidth * imageScale;
      const visibleImageLeft = pandaRect.left + (pandaRect.width - renderedImageWidth) / 2;
      const horizontalGap = Number.parseFloat(
        window.getComputedStyle(this.#menu).getPropertyValue('--panda-menu-horizontal-gap')
      ) || 0;
      const anchoredLeft = visibleImageLeft - horizontalGap - menuRect.width;
      const minimumTop = viewportTop + viewportMargin;
      const minimumLeft = viewportLeft + viewportMargin;
      const maximumTop = Math.max(minimumTop, viewportTop + viewportHeight - viewportMargin - menuRect.height);
      const maximumLeft = Math.max(minimumLeft, viewportLeft + viewportWidth - viewportMargin - menuRect.width);
      const safeTop = Math.min(Math.max(menuRect.top, minimumTop), maximumTop);
      const safeLeft = Math.min(Math.max(anchoredLeft, minimumLeft), maximumLeft);

      this.#menu.style.top = `${Math.round(safeTop)}px`;
      this.#menu.style.bottom = 'auto';
      this.#menu.style.left = `${Math.round(safeLeft)}px`;
      this.#menu.style.right = 'auto';
    }

    #updateActiveMenuItem() {
      let activeKey = null;
      this.#sections.forEach((section) => {
        if (section.state === this.#currentSectionState) activeKey = section.key;
      });

      this.#menuItems.forEach((item, key) => {
        const isActive = key === activeKey;
        item.classList.toggle('is-active', isActive);
        if (isActive) item.setAttribute('aria-current', 'location');
        else item.removeAttribute('aria-current');
      });
    }

    #updateSectionBubble() {
      if (
        !this.#bubble ||
        this.#bubble.dataset.contextEnabled !== 'true' ||
        this.#introActive
      ) return;

      const currentSection = this.#sections.find(
        (section) => section.state === this.#currentSectionState
      );
      if (!currentSection || currentSection.key === this.#currentBubbleSection) return;

      this.#currentBubbleSection = currentSection.key;
      if (this.#bubbleSeen.has(currentSection.key)) {
        this.#hideBubble();
        return;
      }

      const messageKey = `message${currentSection.key
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('')}`;
      const message = this.#bubble.dataset[messageKey];
      this.#bubbleSeen.add(currentSection.key);
      if (!message) {
        this.#hideBubble();
        return;
      }

      const duration = Number.parseInt(this.#bubble.dataset.duration, 10) || 4000;
      this.#showBubble(message, duration);
    }

    #showBubble(message, duration, onHide) {
      if (!this.#bubble || !message) return;

      window.clearTimeout(this.#bubbleTimer);
      this.#bubble.textContent = message;
      this.#bubble.setAttribute('aria-hidden', 'false');
      this.#bubble.classList.add('is-visible');
      this.#positionBubble();
      this.#bubbleTimer = window.setTimeout(() => {
        this.#hideBubble();
        onHide?.();
      }, duration);
    }

    #hideBubble() {
      window.clearTimeout(this.#bubbleTimer);
      this.#bubbleTimer = 0;
      this.#bubble?.classList.remove('is-visible');
      this.#bubble?.setAttribute('aria-hidden', 'true');
    }

    #shouldShowFirstVisitGuide() {
      if (
        this.#root.dataset.firstVisitEnabled !== 'true' ||
        this.#root.dataset.firstVisitBubbleEnabled !== 'true' ||
        !this.#bubble
      ) return false;

      try {
        return window.localStorage.getItem(INTRO_STORAGE_KEY) !== 'true';
      } catch (_error) {
        return true;
      }
    }

    #startFirstVisitGuide() {
      const message = this.#root.dataset.firstVisitMessage || "👋 Hi! I'm Qi Bao.\n\nClick me to explore Chengdu.";
      const customDuration = Number.parseInt(
        this.#root.dataset.firstVisitBubbleDuration,
        10
      );
      const duration = customDuration > 0
        ? customDuration
        : Number.parseInt(this.#root.dataset.firstVisitDuration, 10) || 4000;

      this.setState('wave');
      this.#showBubble(message, duration, () => {
        this.#introActive = false;
        this.setState(this.#currentSectionState);
        this.#updateSectionBubble();
        this.#registerInteraction();
      });
    }

    #completeFirstVisitGuide() {
      if (
        this.#root.dataset.firstVisitEnabled !== 'true' &&
        this.#root.dataset.clickIndicatorEnabled !== 'true'
      ) return;

      try {
        window.localStorage.setItem(INTRO_STORAGE_KEY, 'true');
      } catch (_error) {
        // Storage may be unavailable in privacy-restricted browsers.
      }

      window.clearTimeout(this.#indicatorDelayTimer);
      window.clearTimeout(this.#secondReminderTimer);
      this.#indicatorDelayTimer = 0;
      this.#secondReminderTimer = 0;
      this.#secondReminderResolved = true;
      this.#hideClickIndicator();
      if (!this.#introActive) return;
      this.#introActive = false;
      this.#hideBubble();
      this.setState(this.#currentSectionState);
    }

    #registerInteraction() {
      if (
        this.#root.dataset.idleReminderEnabled !== 'true' ||
        this.#idleReminderShown ||
        this.#introActive
      ) return;

      window.clearTimeout(this.#idleReminderTimer);
      const delay = Math.max(
        0,
        Number.parseInt(this.#root.dataset.idleReminderDelay, 10) || 20000
      );
      this.#idleReminderTimer = window.setTimeout(() => {
        this.#idleReminderTimer = 0;
        if (this.#idleReminderShown || this.#introActive) return;

        this.#idleReminderShown = true;
        const duration = Number.parseInt(this.#bubble?.dataset.duration, 10) || 4000;
        this.#showBubble(
          this.#root.dataset.idleReminderMessage || 'Need help?\n\nClick me!',
          duration
        );
      }, delay);
    }

    #startClickIndicator() {
      if (
        this.#root.dataset.clickIndicatorEnabled !== 'true' ||
        !this.#indicator
      ) return;

      if (this.#hasPandaBeenClicked()) return;

      const delay = Math.max(
        0,
        Number.parseInt(this.#root.dataset.firstReminderDelay, 10) || 1500
      );
      this.#indicatorDelayTimer = window.setTimeout(() => {
        this.#indicatorDelayTimer = 0;
        this.#showClickIndicator(false);
      }, delay);
      this.#scheduleSecondClickReminder();
    }

    #showClickIndicator(isSecondReminder) {
      if (this.#hasPandaBeenClicked() || !this.#indicator) return;

      const styles = window.getComputedStyle(this.#indicator);
      const animationSpeed = Number.parseFloat(
        styles.getPropertyValue('--panda-indicator-speed')
      ) || 1500;
      const configuredDuration = Number.parseInt(
        this.#root.dataset.indicatorDuration,
        10
      ) || 3000;
      const duration = Math.max(configuredDuration, animationSpeed * 2);

      if (!isSecondReminder) this.#firstIndicatorShown = true;
      this.#indicator.classList.remove('is-visible');
      void this.#indicator.offsetWidth;
      this.#indicator.classList.add('is-visible');
      this.#positionIndicator();
      window.clearTimeout(this.#indicatorTimer);
      this.#indicatorTimer = window.setTimeout(() => {
        this.#hideClickIndicator();
        if (!isSecondReminder && this.#reminderSections.size >= 3) {
          this.#attemptSecondClickReminder();
        }
      }, duration);
    }

    #scheduleSecondClickReminder() {
      window.clearTimeout(this.#secondReminderTimer);
      const minimumDelay = Math.max(
        0,
        Number.parseInt(this.#root.dataset.secondReminderDelay, 10) || 20000
      );
      const randomizedDelay = minimumDelay + Math.random() * minimumDelay;
      this.#secondReminderTimer = window.setTimeout(() => {
        this.#secondReminderTimer = 0;
        this.#attemptSecondClickReminder();
      }, randomizedDelay);
    }

    #attemptSecondClickReminder() {
      if (
        this.#secondReminderResolved ||
        !this.#firstIndicatorShown ||
        this.#hasPandaBeenClicked()
      ) return;

      this.#secondReminderResolved = true;
      window.clearTimeout(this.#secondReminderTimer);
      this.#secondReminderTimer = 0;
      const probability = Math.min(
        100,
        Math.max(0, Number.parseInt(this.#root.dataset.reminderProbability, 10) || 0)
      );
      if (Math.random() * 100 < probability) this.#showClickIndicator(true);
    }

    #trackReminderSection() {
      const currentSection = this.#sections.find(
        (section) => section.state === this.#currentSectionState
      );
      if (!currentSection) return;

      this.#reminderSections.add(currentSection.key);
      if (this.#reminderSections.size >= 3 && this.#firstIndicatorShown) {
        this.#attemptSecondClickReminder();
      }
    }

    #hasPandaBeenClicked() {
      if (this.#root.dataset.clickIndicatorDebug === 'true') return false;

      try {
        return window.localStorage.getItem(INTRO_STORAGE_KEY) === 'true';
      } catch (_error) {
        return false;
      }
    }

    #hideClickIndicator() {
      window.clearTimeout(this.#indicatorTimer);
      this.#indicatorTimer = 0;
      this.#indicator?.classList.remove('is-visible');
    }

    #positionIndicator() {
      if (!this.#indicator) return;

      const pandaRect = this.#root.getBoundingClientRect();
      const activeAsset = this.#root.querySelector('.panda-guide__image.is-active .panda-guide__asset');
      const naturalWidth = activeAsset?.naturalWidth || pandaRect.width;
      const naturalHeight = activeAsset?.naturalHeight || pandaRect.height;
      const imageScale = Math.min(
        pandaRect.width / naturalWidth,
        pandaRect.height / naturalHeight
      );
      const renderedWidth = naturalWidth * imageScale;
      const renderedHeight = naturalHeight * imageScale;
      const imageLeft = pandaRect.left + (pandaRect.width - renderedWidth) / 2;
      const imageTop = pandaRect.top + (pandaRect.height - renderedHeight) / 2;
      const visibleBounds = this.#getVisibleBounds(activeAsset);
      const visibleCenterX = visibleBounds
        ? imageLeft + renderedWidth * visibleBounds.centerX
        : imageLeft + renderedWidth / 2;
      const visibleTop = visibleBounds
        ? imageTop + renderedHeight * visibleBounds.top
        : imageTop;
      const visibleBottom = visibleBounds
        ? imageTop + renderedHeight * visibleBounds.bottom
        : imageTop + renderedHeight;
      const styles = window.getComputedStyle(this.#indicator);
      const offsetX = Number.parseFloat(
        styles.getPropertyValue('--panda-indicator-offset-x')
      ) || 0;
      const offsetY = Number.parseFloat(
        styles.getPropertyValue('--panda-indicator-offset-y')
      ) || 0;
      const indicatorRect = this.#indicator.getBoundingClientRect();
      const viewportMargin = 16;
      const desiredLeft = visibleCenterX + offsetX - indicatorRect.width / 2;
      const desiredTop = (visibleTop + visibleBottom) / 2 + offsetY - indicatorRect.height / 2;
      const safeLeft = Math.min(
        Math.max(desiredLeft, viewportMargin),
        window.innerWidth - viewportMargin - indicatorRect.width
      );
      const safeTop = Math.min(
        Math.max(desiredTop, viewportMargin),
        window.innerHeight - viewportMargin - indicatorRect.height
      );

      this.#indicator.style.left = `${Math.round(safeLeft)}px`;
      this.#indicator.style.top = `${Math.round(safeTop)}px`;
    }

    #positionPlatform() {
      if (!this.#platform) return;

      const pandaRect = this.#root.getBoundingClientRect();
      const activeAsset = this.#root.querySelector('.panda-guide__image.is-active .panda-guide__asset');
      const naturalWidth = activeAsset?.naturalWidth || pandaRect.width;
      const naturalHeight = activeAsset?.naturalHeight || pandaRect.height;
      const imageScale = Math.min(
        pandaRect.width / naturalWidth,
        pandaRect.height / naturalHeight
      );
      const renderedWidth = naturalWidth * imageScale;
      const renderedHeight = naturalHeight * imageScale;
      const imageLeft = pandaRect.left + (pandaRect.width - renderedWidth) / 2;
      const imageTop = pandaRect.top + (pandaRect.height - renderedHeight) / 2;
      const visibleBounds = this.#getVisibleBounds(activeAsset);
      const visibleCenterX = visibleBounds
        ? imageLeft + renderedWidth * visibleBounds.centerX
        : imageLeft + renderedWidth / 2;
      const visibleBottom = visibleBounds
        ? imageTop + renderedHeight * visibleBounds.bottom
        : imageTop + renderedHeight;
      const platformWidth = this.#platform.offsetWidth;
      const platformHeight = this.#platform.offsetHeight;
      const styles = window.getComputedStyle(this.#platform);
      const offsetX = Number.parseFloat(styles.getPropertyValue('--qi-platform-offset-x')) || 0;
      const offsetY = Number.parseFloat(styles.getPropertyValue('--qi-platform-offset-y')) || 0;
      const pandaOffsetX = Number.parseFloat(styles.getPropertyValue('--qi-panda-offset-x')) || 0;
      const pandaOffsetY = Number.parseFloat(styles.getPropertyValue('--qi-panda-offset-y')) || 0;

      this.#platform.style.left = `${Math.round(
        visibleCenterX - platformWidth / 2 + offsetX - pandaOffsetX
      )}px`;
      this.#platform.style.top = `${Math.round(
        visibleBottom - platformHeight * 0.55 + offsetY - pandaOffsetY
      )}px`;
    }

    #positionBodyGlow() {
      if (!this.#bodyGlow) return;

      const pandaRect = this.#root.getBoundingClientRect();
      const activeAsset = this.#root.querySelector('.panda-guide__image.is-active .panda-guide__asset');
      const naturalWidth = activeAsset?.naturalWidth || pandaRect.width;
      const naturalHeight = activeAsset?.naturalHeight || pandaRect.height;
      const imageScale = Math.min(
        pandaRect.width / naturalWidth,
        pandaRect.height / naturalHeight
      );
      const renderedWidth = naturalWidth * imageScale;
      const renderedHeight = naturalHeight * imageScale;
      const imageLeft = pandaRect.left + (pandaRect.width - renderedWidth) / 2;
      const imageTop = pandaRect.top + (pandaRect.height - renderedHeight) / 2;
      const visibleBounds = this.#getVisibleBounds(activeAsset);
      const visibleCenterX = visibleBounds
        ? imageLeft + renderedWidth * visibleBounds.centerX
        : imageLeft + renderedWidth / 2;
      const visibleTop = visibleBounds
        ? imageTop + renderedHeight * visibleBounds.top
        : imageTop;
      const visibleBottom = visibleBounds
        ? imageTop + renderedHeight * visibleBounds.bottom
        : imageTop + renderedHeight;

      this.#bodyGlow.style.left = `${Math.round(
        visibleCenterX - this.#bodyGlow.offsetWidth / 2
      )}px`;
      this.#bodyGlow.style.top = `${Math.round(
        (visibleTop + visibleBottom) / 2 - this.#bodyGlow.offsetHeight / 2
      )}px`;
    }

    #getVisibleBounds(asset) {
      const source = asset?.currentSrc || asset?.src;
      if (!source) return null;
      if (this.#visibleBounds.has(source)) return this.#visibleBounds.get(source);

      this.#visibleBounds.set(source, null);
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = probe.naturalWidth;
          canvas.height = probe.naturalHeight;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(probe, 0, 0);
          const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
          const bodyStartY = Math.floor(canvas.height * 0.38);
          let top = canvas.height;
          let bottom = 0;
          let weightedX = 0;
          let totalWeight = 0;

          for (let y = 0; y < canvas.height; y += 1) {
            for (let x = 0; x < canvas.width; x += 1) {
              const alpha = pixels[(y * canvas.width + x) * 4 + 3];
              if (alpha <= 8) continue;
              top = Math.min(top, y);
              bottom = Math.max(bottom, y + 1);
              if (y >= bodyStartY) {
                weightedX += x * alpha;
                totalWeight += alpha;
              }
            }
          }

          this.#visibleBounds.set(source, {
            centerX: totalWeight ? weightedX / totalWeight / canvas.width : 0.5,
            top: top < canvas.height ? top / canvas.height : 0,
            bottom: bottom ? bottom / canvas.height : 1
          });
          this.#positionPlatform();
          this.#positionBodyGlow();
        } catch (_error) {
          this.#visibleBounds.set(source, null);
        }
      };
      probe.onerror = () => {
        this.#visibleBounds.set(source, null);
      };
      probe.src = source;
      return null;
    }

    #schedulePulse() {
      window.clearTimeout(this.#pulseTimer);
      if (this.#root.dataset.pulseEnabled !== 'true') return;

      const interval = Math.max(
        1000,
        Number.parseInt(this.#root.dataset.pulseInterval, 10) || 8000
      );
      this.#pulseTimer = window.setTimeout(() => {
        this.#pulseTimer = 0;
        const canPulse =
          !this.#menuOpen &&
          !this.#introActive &&
          this.#currentState !== 'walk' &&
          !this.#root.matches(':hover') &&
          document.visibilityState === 'visible';

        if (canPulse) {
          this.#root.classList.remove('is-pulsing');
          this.#platform?.classList.remove('is-pulsing');
          void this.#root.offsetWidth;
          this.#root.classList.add('is-pulsing');
          this.#platform?.classList.add('is-pulsing');
          window.clearTimeout(this.#pulseEndTimer);
          this.#pulseEndTimer = window.setTimeout(() => {
            this.#pulseEndTimer = 0;
            this.#root.classList.remove('is-pulsing');
            this.#platform?.classList.remove('is-pulsing');
          }, 500);
        }

        this.#schedulePulse();
      }, interval);
    }

    #positionBubble() {
      if (!this.#bubble) return;

      const viewportMargin = 24;
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportWidth = viewport?.width || window.innerWidth;
      const pandaRect = this.#root.getBoundingClientRect();
      const activeAsset = this.#root.querySelector('.panda-guide__image.is-active .panda-guide__asset');
      const naturalWidth = activeAsset?.naturalWidth || pandaRect.width;
      const naturalHeight = activeAsset?.naturalHeight || pandaRect.height;
      const imageScale = Math.min(
        pandaRect.width / naturalWidth,
        pandaRect.height / naturalHeight
      );
      const renderedWidth = naturalWidth * imageScale;
      const renderedHeight = naturalHeight * imageScale;
      const imageLeft = pandaRect.left + (pandaRect.width - renderedWidth) / 2;
      const imageTop = pandaRect.top + (pandaRect.height - renderedHeight) / 2;
      const imageCenter = imageLeft + renderedWidth / 2;
      const styles = window.getComputedStyle(this.#bubble);
      const horizontalOffset = Number.parseFloat(
        styles.getPropertyValue('--panda-bubble-horizontal-offset')
      ) || 0;
      const verticalOffset = Number.parseFloat(
        styles.getPropertyValue('--panda-bubble-vertical-offset')
      ) || 0;
      const firstVisitOffsetX = this.#introActive
        ? Number.parseFloat(styles.getPropertyValue('--panda-first-bubble-offset-x')) || 0
        : 0;
      const firstVisitOffsetY = this.#introActive
        ? Number.parseFloat(styles.getPropertyValue('--panda-first-bubble-offset-y')) || 0
        : 0;
      const bubbleRect = this.#bubble.getBoundingClientRect();
      const minimumLeft = viewportLeft + viewportMargin;
      const maximumLeft = Math.max(
        minimumLeft,
        viewportLeft + viewportWidth - viewportMargin - bubbleRect.width
      );
      const minimumTop = viewportTop + viewportMargin;
      const maximumTop = Math.max(
        minimumTop,
        viewportTop + viewportHeight - viewportMargin - bubbleRect.height
      );
      const desiredLeft = imageCenter - bubbleRect.width / 2 + horizontalOffset + firstVisitOffsetX;
      const desiredTop = imageTop - bubbleRect.height - 12 + verticalOffset + firstVisitOffsetY;
      const safeLeft = Math.min(Math.max(desiredLeft, minimumLeft), maximumLeft);
      const safeTop = Math.min(Math.max(desiredTop, minimumTop), maximumTop);
      const tailPosition = Math.min(
        Math.max(imageCenter - safeLeft, 14),
        bubbleRect.width - 14
      );

      this.#bubble.style.left = `${Math.round(safeLeft)}px`;
      this.#bubble.style.top = `${Math.round(safeTop)}px`;
      this.#bubble.style.setProperty('--panda-bubble-tail-x', `${Math.round(tailPosition)}px`);
    }

    #setAnimation(_currentState, _previousState) {
      // Reserved for future sprite-sequence animation.
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.('[data-panda-guide]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-panda-guide]'));

    roots.forEach((root) => {
      if (instances.has(root) || root.dataset.pandaInitialized === 'true') return;

      const instance = new PandaGuide(root);
      instances.set(root, instance);
      root.dataset.pandaInitialized = 'true';
      instance.init();
    });
  };

  const start = () => {
    initialize();
    document.addEventListener('shopify:section:load', (event) => {
      initialize(event.target);
    });
  };

  start();
})();
